'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { IconButton, Typography } from '@mui/material';
import SelectionMatrix from 'src/components/SelectionMatrix';
import { Spinner } from '@repo/components';
import { Role, Zone, RolesApiResponse, ZonesApiResponse } from '@repo/shared/domain';
import { lightTheme } from '@repo/theme';
import { guardedClient as clientRequest } from '@repo/shared/helpers';

export const Route = createFileRoute('/_admin/config/roles/access')({
	component: AccessPage,
});

const getRoles = async (): Promise<RolesApiResponse> => {
	const { body } = await clientRequest.get<undefined, RolesApiResponse>('/api/roles?noPagination=true', { schema: undefined });
	return body;
};

const getZones = async (): Promise<ZonesApiResponse> => {
	const { body } = await clientRequest.get<undefined, ZonesApiResponse>('/api/zones?noPagination=true', { schema: undefined });
	return body;
};

function AccessPage() {
	const navigate = useNavigate();

	const { data: rolesData, isLoading: rolesLoading } = useQuery({
		queryKey: ['roles-all'],
		queryFn: getRoles,
	});

	const { data: zonesData, isLoading: zonesLoading } = useQuery({
		queryKey: ['zones-all'],
		queryFn: getZones,
	});

	const roles: Role[] = rolesData?.data || [];
	const zones: Zone[] = zonesData?.data || [];

	const selectionOptions = [
		{
			id: 'zoneIds',
			label: 'Zone accessible',
			color: lightTheme.palette.secondary.main,
		},
		{
			id: 'emergencyZoneIds',
			label: 'Zone accessible during emergency',
			color: 'red',
		},
	];

	const rows = roles.map((role) => {
		return {
			id: role.id,
			name: role.name,
			disabled: role.showAccessCode === 'never',
			values: [
				{
					optionId: 'zoneIds',
					selected: role.zoneIds || [],
				},
				{
					optionId: 'emergencyZoneIds',
					selected: role.emergencyZoneIds || [],
				},
			],
		};
	});

	const isLoading = rolesLoading || zonesLoading;
	if (isLoading) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	return (
		<div className="h-full w-full flex flex-col">
			<div className="h-full w-full p-8 flex flex-col gap-4">
				<div className="flex flex-row flex-wrap gap-2 justify-between">
					<div className="min-h-42 flex flex-row gap-2">
						<IconButton onClick={() => navigate({ to: '/config/roles' })}>
							<ArrowBackIcon />
						</IconButton>
						<Typography variant="h4">Role Access Grid</Typography>
					</div>
				</div>
				<SelectionMatrix
					headers={zones.map((z) => {
						return { id: z.id, name: z.name };
					})}
					options={selectionOptions}
					rows={rows}
				/>
			</div>
		</div>
	);
}
